/**
 * Document History Modal Component
 */
import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { History, RefreshCw, Download } from 'lucide-react';
import { format, parseISO } from 'date-fns';

const STATUS_STYLES = {
  pending: 'bg-[#FFC04D]/15 text-[#FFC04D] border-[#FFC04D]/20',
  approved: 'bg-[#1ABC9C]/15 text-[#1ABC9C] border-[#1ABC9C]/20',
  rejected: 'bg-red-500/15 text-red-400 border-red-500/20',
  expired: 'bg-gray-500/15 text-gray-400 border-gray-500/20',
};

export function DocumentHistoryModal({
  open,
  onOpenChange,
  documentHistory,
  loading
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#2B2B2B] border-white/10 text-[#F2F2F2] max-w-lg max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="w-5 h-5 text-[#1ABC9C]" />
            Document History
          </DialogTitle>
          <DialogDescription className="text-[#A1A1AA]">
            {documentHistory?.document_type} - {documentHistory?.total_versions} version(s)
          </DialogDescription>
        </DialogHeader>
        
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="w-6 h-6 animate-spin text-[#1ABC9C]" />
          </div>
        ) : documentHistory?.versions?.length > 0 ? (
          <div className="space-y-3">
            {documentHistory.versions.map((version, idx) => (
              <div 
                key={version.id}
                className={`p-3 rounded-lg border ${
                  version.is_current 
                    ? 'bg-[#1ABC9C]/10 border-[#1ABC9C]/30' 
                    : 'bg-[#1E1E1E] border-white/5'
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Badge className={version.is_current 
                      ? 'bg-[#1ABC9C]/15 text-[#1ABC9C] border-[#1ABC9C]/20'
                      : 'bg-gray-500/15 text-gray-400 border-gray-500/20'
                    }>
                      v{version.version}
                    </Badge>
                    {version.is_current && (
                      <span className="text-xs text-[#1ABC9C]">Current</span>
                    )}
                  </div>
                  <Badge className={STATUS_STYLES[version.status] || 'bg-gray-500/15 text-gray-400'}>
                    {version.status}
                  </Badge>
                </div>
                
                <p className="text-sm text-[#F2F2F2] truncate">{version.file_name}</p>
                
                <div className="flex items-center justify-between mt-2 text-xs text-[#A1A1AA]">
                  <span>Uploaded by {version.uploaded_by_name}</span>
                  <span>{format(parseISO(version.created_at), 'dd/MM/yyyy HH:mm')}</span>
                </div>
                
                {version.expires_at && (
                  <p className="text-xs text-[#A1A1AA] mt-1">
                    Expires: {format(parseISO(version.expires_at), 'dd/MM/yyyy')}
                  </p>
                )}
                
                {version.rejection_reason && (
                  <p className="text-xs text-red-400 mt-1">
                    Rejected: {version.rejection_reason}
                  </p>
                )}
                
                {version.file_url && (
                  <a 
                    href={version.file_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-[#1ABC9C] hover:underline mt-2"
                  >
                    <Download className="w-3 h-3" />
                    Download
                  </a>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-center text-[#A1A1AA] py-4">No version history available</p>
        )}
      </DialogContent>
    </Dialog>
  );
}
